import { captureSentryError } from "JS/Helpers/SentryHelper";
import { FBCourseStats, FBStepStats } from "JS/Models/Firebase/Courses";
import {
    observeCourseStats,
    observeStepStats,
} from "JS/Services/Firebase/Observer";
import { Unsubscribe } from "firebase/database";
import { isEqual } from "lodash-es";
import { useEffect, useState } from "react";
import { useFirebaseAuth } from "./Auth";

export const useObserveCourseStats = (sku: string) => {
    const { coursesFirebaseSignIn } = useFirebaseAuth();
    const [observedCourseStats, setObservedCourseStats] =
        useState<FBCourseStats>(null);

    useEffect(() => {
        if (!sku) return;

        let unsubscribe: Unsubscribe | undefined;
        let cancelled = false;

        coursesFirebaseSignIn()
            .then(() =>
                observeCourseStats(sku, (stats) => {
                    // updater form — the effect closure would hold a stale value
                    setObservedCourseStats((prev) =>
                        isEqual(stats, prev) ? prev : stats,
                    );
                }),
            )
            .then((unsub) => {
                // unmounted before the subscription resolved
                if (cancelled) unsub?.();
                else unsubscribe = unsub;
            })
            .catch((err) => {
                captureSentryError(err, {
                    location: `useObserveCourseStats`,
                });
            });

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [sku]);

    return { observedCourseStats };
};

export const useObserveStepStats = (courseSku: string, stepSku: string) => {
    const [observedStepStats, setObservedStepStats] =
        useState<FBStepStats>(null);

    useEffect(() => {
        if (!courseSku || !stepSku) return;

        let unsubscribe: Unsubscribe | undefined;
        let cancelled = false;

        observeStepStats(courseSku, stepSku, (stats) => {
            setObservedStepStats((prev) => (isEqual(stats, prev) ? prev : stats));
        })
            .then((unsub) => {
                if (cancelled) unsub?.();
                else unsubscribe = unsub;
            })
            .catch((err) => {
                captureSentryError(err, {
                    location: `useObserveStepStats`,
                });
            });

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [courseSku, stepSku]);

    return { observedStepStats };
};
